/**
 * ASCII Art Generator - 引擎动画辅助函数
 *
 * 提供引擎渲染时可以使用的动画计算函数
 * Wave 8.3: 支持灰度缓存
 */

import type { AnimationContext } from '../animation/types'
import type { AnimationType, FullRenderSettings, SampledPixel } from '../types'
import type { MouseState } from '../interaction/types'
import type { GrayscaleCacheData } from '../algorithms/grayscale'
import { samplePixel, samplePixelFast } from '../algorithms/grayscale'
import { isGrayscaleCacheEnabled } from '../performance/features'
import { calculateWaveOffset, createWaveConfigFromSettings } from '../animation/WaveEffect'
import { calculatePulseFactor, createPulseConfigFromSettings } from '../animation/PulseEffect'
import { calculateBreatheEffect, createBreatheConfigFromSettings } from '../animation/BreatheEffect'
import { calculateBlinkEffect, createBlinkConfigFromSettings } from '../animation/BlinkEffect'
import { calculateRainbowEffect, createRainbowConfigFromSettings } from '../animation/RainbowEffect'
import { calculateLoopEffect, createLoopConfigFromSettings } from '../animation/LoopEffect'
import { calculateMouseOffset } from '../interaction/MouseInteraction'

/**
 * 字符位置动画偏移结果
 */
export interface CharacterAnimationResult {
  /** X 方向偏移（像素） */
  offsetX: number
  /** Y 方向偏移（像素） */
  offsetY: number
  /** 脉动因子 (0-2，1为基准) */
  pulseFactor: number
  /** 鼠标交互强度 (0-1) */
  mouseInfluence: number
  /** 抖动偏移 */
  jitterX: number
  jitterY: number
  /** 呼吸效果 - 亮度因子 */
  brightnessFactor: number
  /** 呼吸效果 - 透明度因子 */
  opacityFactor: number
  /** 呼吸效果 - 缩放因子 */
  scale: number
  /** 彩虹颜色 */
  rainbowColor: { r: number; g: number; b: number } | null
  /** 闪烁可见性 */
  blinkVisible: boolean
}

/**
 * 计算随机抖动
 */
function calculateJitter(intensity: number, time: number, seed: number): { x: number; y: number } {
  // 使用噪声函数产生随机抖动
  const seed1 = Math.sin(time * 12.9898 + seed * 78.233) * 43758.5453
  const seed2 = Math.sin(time * 78.233 + seed * 12.9898) * 43758.5453
  const jitterX = (seed1 - Math.floor(seed1) - 0.5) * intensity * 2
  const jitterY = (seed2 - Math.floor(seed2) - 0.5) * intensity * 2
  return { x: jitterX, y: jitterY }
}

/**
 * 计算漂移效果
 */
function calculateDrift(
  charX: number,
  charY: number,
  time: number,
  intensity: number,
  speed: number = 0.5
): { x: number; y: number } {
  // 基于位置的缓慢漂移，速度参数控制漂移频率
  const driftX = Math.sin(time * speed + charX * 0.01) * intensity * 5
  const driftY = Math.cos(time * speed * 0.8 + charY * 0.01) * intensity * 5
  return { x: driftX, y: driftY }
}

/**
 * 计算字符位置的动画偏移
 */
export function calculateCharacterAnimation(
  charX: number,
  charY: number,
  context: AnimationContext,
  settings: FullRenderSettings,
  charWidth: number = 10,
  charHeight: number = 10
): CharacterAnimationResult {
  // 默认结果
  const result: CharacterAnimationResult = {
    offsetX: 0,
    offsetY: 0,
    pulseFactor: 1,
    mouseInfluence: 0,
    jitterX: 0,
    jitterY: 0,
    brightnessFactor: 1,
    opacityFactor: 1,
    scale: 1,
    rainbowColor: null,
    blinkVisible: true
  }

  // 检查动画是否启用
  const animationEnabled = settings.ditherAnimationEnabled || settings.dither2AnimationEnabled

  if (!animationEnabled) {
    return result
  }

  // 获取动画类型
  const animationType: AnimationType = settings.animationType || 'wave'

  // 获取振幅参数
  const amplitude = settings.ditherWaveAmplitude || settings.dither2WaveAmplitude || 0.3

  // 根据动画类型计算效果
  switch (animationType) {
    case 'wave':
      // 波浪动画
      if (amplitude > 0) {
        const frequency = settings.ditherWaveFrequency || settings.dither2WaveFrequency || 3
        const speed = settings.ditherWaveSpeed || settings.dither2WaveSpeed || 0.05
        const waveConfig = createWaveConfigFromSettings(amplitude, frequency, speed, charWidth, charHeight)
        const waveOffset = calculateWaveOffset(charX, charY, context.time, waveConfig)
        result.offsetX += waveOffset.offsetX
        result.offsetY += waveOffset.offsetY
      }
      // 波浪模式也添加轻微抖动
      {
        const jitter = calculateJitter(amplitude * 0.5, context.time, charX + charY * 1000)
        result.offsetX += jitter.x
        result.offsetY += jitter.y
      }
      break

    case 'pulse':
      // 脉动效果 - 主要影响亮度，轻微位置偏移
      {
        const frequency = settings.ditherWaveFrequency || settings.dither2WaveFrequency || 1.2
        const pulseConfig = createPulseConfigFromSettings(amplitude * 0.5, frequency)
        result.pulseFactor = calculatePulseFactor(context.time, pulseConfig)
        // 轻微的缩放效果
        const scale = 1 + Math.sin(context.time * 3) * amplitude * 0.1
        result.offsetX = (charX - charX * scale)
        result.offsetY = (charY - charY * scale)
      }
      break

    case 'jitter':
      // 纯抖动效果
      {
        const jitter = calculateJitter(amplitude * 8, context.time, charX + charY * 1000)
        result.offsetX = jitter.x
        result.offsetY = jitter.y
        result.jitterX = jitter.x
        result.jitterY = jitter.y
      }
      break

    case 'drift':
      // 漂移效果
      {
        const speed = settings.ditherWaveSpeed || settings.dither2WaveSpeed || 0.5
        const drift = calculateDrift(charX, charY, context.time, amplitude, speed)
        result.offsetX = drift.x
        result.offsetY = drift.y
        // 漂移也添加轻微抖动
        const jitter = calculateJitter(amplitude * 2, context.time * 0.5, charX + charY * 1000)
        result.offsetX += jitter.x
        result.offsetY += jitter.y
      }
      break

    case 'breathe':
      // 局部呼吸效果 - 影响亮度/透明度/大小
      {
        const frequency = settings.ditherWaveFrequency || settings.dither2WaveFrequency || 0.5
        const breatheConfig = createBreatheConfigFromSettings(amplitude, frequency, 0.01)
        const breatheResult = calculateBreatheEffect(charX, charY, context.time, breatheConfig)
        result.brightnessFactor = breatheResult.brightnessFactor
        result.opacityFactor = breatheResult.opacityFactor
        result.scale = breatheResult.scale
      }
      break

    case 'blink':
      // 闪烁效果
      {
        const speed = settings.ditherWaveFrequency || settings.dither2WaveFrequency || 2
        const blinkConfig = createBlinkConfigFromSettings(amplitude, speed, amplitude)
        const blinkResult = calculateBlinkEffect(charX, charY, context.time, blinkConfig)
        result.brightnessFactor = blinkResult.brightness
        result.blinkVisible = blinkResult.visible
      }
      break

    case 'rainbow':
      // 彩虹效果 - 基于位置的颜色变化
      {
        const speed = settings.ditherWaveSpeed || settings.dither2WaveSpeed || 1
        const rainbowConfig = createRainbowConfigFromSettings(speed, 0.8)
        result.rainbowColor = calculateRainbowEffect(charX, charY, context.time, rainbowConfig)
      }
      break

    case 'loop':
      // 循环路径效果
      {
        const speed = settings.ditherWaveSpeed || settings.dither2WaveSpeed || 1
        const loopConfig = createLoopConfigFromSettings(amplitude * 5, speed)
        const loopResult = calculateLoopEffect(charX, charY, context.time, loopConfig)
        result.offsetX = loopResult.offsetX
        result.offsetY = loopResult.offsetY
      }
      break

    case 'comet':
      // 彗星效果 - 从左到右移动的亮带
      {
        const speed = settings.ditherWaveSpeed || settings.dither2WaveSpeed || 1
        // 使用时间和X位置创建从左到右移动的波
        const cometX = (context.time * speed * 100) % (charX + 500)
        const distFromComet = Math.abs(charX - cometX)
        const cometWidth = 50 + amplitude * 100
        if (distFromComet < cometWidth) {
          // 在彗星范围内，亮度增强
          const tailFactor = 1 - distFromComet / cometWidth
          result.brightnessFactor = 1 + tailFactor * amplitude * 2
          result.opacityFactor = 0.5 + tailFactor * 0.5
          // 轻微Y方向偏移
          result.offsetY = Math.sin(context.time * 2) * amplitude * 3
        } else {
          result.opacityFactor = 0.3
        }
      }
      break

    case 'audio':
      // 动感音乐效果 - 模拟频谱波动
      {
        const speed = settings.ditherWaveFrequency || settings.dither2WaveFrequency || 2
        // 基于X位置创建不同的频带
        const bandIndex = Math.floor(charX / 50) % 8
        const bandPhase = bandIndex * 0.3
        const wave = Math.sin(context.time * speed + bandPhase)
        const normalizedWave = (wave + 1) / 2 // 0-1
        // Y位置影响是否在波动范围内
        const waveHeight = normalizedWave * amplitude * 30
        const yDist = Math.abs(charY % 100 - 50)
        const inWave = yDist < waveHeight + 20
        if (inWave) {
          result.brightnessFactor = 1 + normalizedWave * amplitude
          result.opacityFactor = 0.6 + normalizedWave * 0.4
        } else {
          result.opacityFactor = 0.4
        }
      }
      break

    case 'stream':
      // 串流式字符效果 - 倾斜滚动
      {
        const speed = settings.ditherWaveSpeed || settings.dither2WaveSpeed || 1
        const angle = 15 * Math.PI / 180 // 倾斜角度
        // 计算倾斜偏移
        const streamOffset = context.time * speed * 50
        const tiltedY = charY + charX * Math.tan(angle)
        const streamPhase = (tiltedY + streamOffset) % 200
        // 创建流动效果
        if (streamPhase < 100) {
          const streamFactor = streamPhase / 100
          result.brightnessFactor = 0.5 + streamFactor * 0.5
          result.opacityFactor = 0.3 + streamFactor * 0.7
        } else {
          const streamFactor = (200 - streamPhase) / 100
          result.brightnessFactor = 0.5 + streamFactor * 0.5
          result.opacityFactor = 0.3 + streamFactor * 0.7
        }
      }
      break

    case 'none':
    default:
      // 无动画效果
      break
  }

  // 鼠标交互偏移（所有动画类型都支持）
  const mouseInteractionEnabled = settings.ditherMouseInteractionEnabled || settings.dither2MouseInteractionEnabled

  if (mouseInteractionEnabled && context.mouseState.isInCanvas) {
    const mouseState: MouseState = {
      x: context.mouseState.x,
      y: context.mouseState.y,
      isInCanvas: context.mouseState.isInCanvas,
      normalizedX: context.mouseState.x / (settings.mouseAreaSize || 180),
      normalizedY: context.mouseState.y / (settings.mouseAreaSize || 180)
    }

    const mouseResult = calculateMouseOffset(charX, charY, mouseState, settings)

    result.offsetX += mouseResult.offsetX
    result.offsetY += mouseResult.offsetY
    result.mouseInfluence = mouseResult.influence
  }

  return result
}

/**
 * 计算字符位置的动画偏移（简化版本）
 */
export function calculateCharacterAnimationOffset(
  charX: number,
  charY: number,
  context: AnimationContext,
  settings: FullRenderSettings,
  charWidth: number = 10,
  charHeight: number = 10
): { offsetX: number; offsetY: number } {
  const result = calculateCharacterAnimation(charX, charY, context, settings, charWidth, charHeight)
  return { offsetX: result.offsetX, offsetY: result.offsetY }
}

/**
 * 应用脉动效果到亮度值
 */
export function applyPulseToCharacterBrightness(
  brightness: number,
  pulseFactor: number
): number {
  const adjusted = brightness * pulseFactor
  return Math.max(0, Math.min(255, Math.round(adjusted)))
}

/**
 * 应用呼吸效果到亮度值
 */
export function applyBreatheToCharacterBrightness(
  brightness: number,
  brightnessFactor: number
): number {
  const adjusted = brightness * brightnessFactor
  return Math.max(0, Math.min(255, Math.round(adjusted)))
}

/**
 * 判断当前是否需要动画渲染
 */
export function shouldRenderAnimated(settings: FullRenderSettings): boolean {
  return (
    settings.ditherAnimationEnabled ||
    settings.dither2AnimationEnabled ||
    settings.ditherMouseInteractionEnabled ||
    settings.dither2MouseInteractionEnabled
  )
}

/**
 * 创建默认的静态动画上下文
 */
export function createStaticContext(): AnimationContext {
  return {
    time: 0,
    deltaTime: 0,
    frameCount: 0,
    mouseState: {
      x: 0,
      y: 0,
      isInCanvas: false
    }
  }
}

// ============================================
// Wave 8.3: 灰度缓存采样辅助函数
// ============================================

/**
 * 扩展的渲染上下文（包含灰度缓存）
 */
export interface RenderContextExt extends AnimationContext {
  grayscaleCache?: GrayscaleCacheData | null
}

/**
 * 智能像素采样（自动使用缓存或原始采样）
 *
 * Wave 8.3 核心函数：如果灰度缓存可用，使用快速采样；
 * 否则使用传统的 samplePixel 函数。
 *
 * @param imageData 图像数据
 * @param context 渲染上下文（包含灰度缓存）
 * @param x 起始X坐标
 * @param y 起始Y坐标
 * @param width 采样宽度
 * @param height 采样高度
 * @returns 采样结果（亮度 + 颜色）
 */
export function smartSamplePixel(
  imageData: ImageData,
  context: RenderContextExt,
  x: number,
  y: number,
  width: number,
  height: number
): SampledPixel {
  // 如果灰度缓存可用且启用，使用快速采样
  if (context.grayscaleCache && isGrayscaleCacheEnabled()) {
    return samplePixelFast(context.grayscaleCache, x, y, width, height)
  }

  // 否则使用传统采样
  return samplePixel(imageData, x, y, width, height)
}

/**
 * 检查渲染上下文中是否有有效的灰度缓存
 */
export function hasGrayscaleCache(context: RenderContextExt): boolean {
  return context.grayscaleCache != null && isGrayscaleCacheEnabled()
}